/**
 * useViewerController — the viewer's logic hook. Owns every hook call, derived
 * memo, effect, and handler that used to live in App's `AppContent`. Returns a
 * single flat bag of state + callbacks consumed by `ViewerComposition` (the
 * render). App owns state and logic through this hook; the composition owns
 * markup. This is the logic half of the App/render seam.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { StableId } from "@archlang/scene/arch-model";
import { useProcessPlaybackContext } from "./ProcessPlaybackContext.js";
import { useBoardHandle } from "./BoardHandleContext.js";
import type { Viewport } from "@archlang/scene/board";
import { useWidgetDiagnostics } from "../hooks/useWidgetDiagnostics";
import {
  buildBoardIndexes,
  buildEdgeSelectLookup,
  resolveSelectedAspect,
  resolveSelectedInterface,
  resolveArchlangNav,
} from "../derivations";
import { type SidebarTab } from "../components/Sidebar";
import { useMode, type Mode } from "../hooks/useMode";
import { useAspectOverlay } from "../hooks/useAspectOverlay";
import { useModelIndexes, findModuleByName } from "../hooks/useModelIndexes";
import { useControlledSources } from "../hooks/useControlledSources";
import { useViewerKeyboard } from "../hooks/useViewerKeyboard";
import { type SearchEntry } from "../search/searchIndex";
import { useSearchModal } from "../search/useSearchModal";
import { usePlaneSwitcher } from "../hooks/usePlaneSwitcher";
import {
  buildAspectColoring,
  buildTypeColoring,
  buildViewColoring,
  type NodeColoring,
} from "../board/coloring/index.js";
import { useActiveView } from "./ActiveViewContext.js";
import { lifecycleWindowsOf, type LifecycleWindow } from "@archlang/engine";
import type { Vfs } from "@archlang/viewer-core";
import { type SourceMode } from "../sources/useSources";
import { DRAFT_PATH, useWorkspaceSources } from "../sources/useWorkspaceSources";
import { type ViewerBootSource } from "../sources/viewerFileSource";
import { useWorkspace } from "./WorkspaceContext.js";
import { useActiveTabId, useActiveTabKind } from "./WorkspaceStore";
import { usePerTabFlag, usePerTabValue } from "../hooks/usePerTabFlag";
import {
  EDITOR_LAYOUT_DEFAULT,
  type EditorLayout,
} from "./primitives/EditorLayoutMenu";
import {
  isEditorLayout,
  defaultLayoutFor,
  layoutOptionsFor,
  EDITOR_LAYOUTS,
  VIEW_MODE_BY_KIND,
  type EditorTabKind,
} from "./editorLayout";
import { useWorkspaceModel } from "./WorkspaceModelContext.js";
import { useViewPersistence } from "../hooks/useViewPersistence";
import { useExportShareActions } from "./useExportShareActions";
import { usePlacementsContext } from "./PlacementsContext.js";
import { useLayoutContext } from "./LayoutContext.js";
import { useCompare } from "./CompareContext.js";
import { useStoreSelection } from "../hooks/useStoreSelection";
import { useMobileLayout } from "../hooks/useMobileLayout";
import { persistedViewerStateAtBoot, type ViewerAppProps } from "../ViewerApp";
import { NO_SURFACES, type SurfaceContext } from "./surface";
import { IS_DEV } from "./isDev";
import { hostStorage } from "../host/hostStorage.js";

interface SourceSlot {
  vfs: Vfs;
  label: string;
}

/** Inputs the store-only workbench controller needs from its host — nothing
 *  about WHERE the files came from (no boot, no source picking, no hash
 *  routing). The viewer wrapper below supplies the viewer-specific values; the
 *  Studio host supplies its changeset equivalents. */
export interface WorkbenchControllerInput {
  /** Hide the left workspace/navigation panel (embeds with their own tree). */
  readonly hideWorkspacePanel?: boolean;
  /** Label for export/share filenames (the source or changeset name). */
  readonly exportLabel?: string;
  /** Seed the details-rail collapsed state (e.g. restored view state). */
  readonly restoredSidebarCollapsed?: boolean | null;
  /** Prefix for localStorage persistence keys (per-repo / per-changeset), so two
   *  workspaces don't share panel/layout state. Default: none (the viewer keys). */
  readonly persistNamespace?: string;
  /** "desktop" pins the panel layout at every width — see `useMobileLayout`. */
  readonly layout?: "auto" | "desktop";
}

/**
 * useWorkbenchController — the SHARED render-driving logic: selection, mode,
 * aspect, search, process, layout, the handlers + derivations. Reads ONLY the
 * store + the providers — knows nothing about boot / source acquisition / hash
 * routing. Both the viewer (`useViewerController`) and the Studio host mount it.
 */
export function useWorkbenchController({
  hideWorkspacePanel = false,
  exportLabel = "workspace",
  restoredSidebarCollapsed,
  persistNamespace,
  layout = "auto",
}: WorkbenchControllerInput = {}) {
  // Per-workspace persistence prefix — Studio passes a repo/changeset scope so
  // panel/layout state doesn't bleed across repos (same-named files otherwise
  // share per-tab records). Empty default preserves the viewer's historical keys.
  const nsPrefix = persistNamespace ? `${persistNamespace}.` : "";
  const detailsCollapsedKey = `${nsPrefix}archlang.detailsPanel.collapsedByTab.v1`;
  const editorLayoutLastKey = `${nsPrefix}archlang.editorLayout.last.v1`;
  const editorLayoutByTabKey = `${nsPrefix}archlang.editorLayout.byTab.v1`;
  const navCollapsedKey = `${nsPrefix}archlang.workspacePanel.collapsed.v1`;
  const store = useWorkspace();
  // Shared board transport (imperative handle + board↔nav hover channel) — owned
  // by BoardHandleProvider so primitives can self-source it instead of prop-drill.
  const { boardRef, hoverStore } = useBoardHandle();
  const { mode, setMode } = useMode(store);
  // Compare ("diff against another version") is the shared CompareProvider
  // service; the diff surface + nav controls read it directly. The controller
  // only needs the current target for the export/share link.
  const { against: compareAgainst, swapped: compareSwapped } = useCompare();

  const {
    model,
    diagnostics,
    edges,
    structural,
    loading,
    error,
    refresh,
    coldRefresh,
    blastRadiusIds,
    documents,
    resolvedModel,
  } = useWorkspaceModel();
  // "Colour by aspect", legend highlight, and "aspect web" overlay — picks
  // in the store's labels.slice, derivations off the loaded model. See
  // `useAspectOverlay`.
  const aspectCtl = useAspectOverlay(store, model);
  const { aspectColorKey } = aspectCtl;
  // v0.9.0 §1.1: plane switcher — its state lives here so a ⌘F "plane" hit can
  // activate it (see `handleSearchPick`); ViewerComposition reads `plane` to
  // render the switcher + focus the board.
  const plane = usePlaneSwitcher(model);
  // Workspace-panel search box. Board background clicks refocus it so the
  // user can type-to-search a node without reaching for the mouse.
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Selection + sidebar drill-down history — store-backed (seeded by the
  // provider from the restored view state). The board viewport pair is the
  // store's ViewportSeam, registered inside the hook. See `useStoreSelection`.
  const selection = useStoreSelection(store, {
    getViewport: useCallback(() => boardRef.current?.getViewport() ?? null, []),
    restoreViewport: useCallback(
      (vp: Viewport) => boardRef.current?.restoreViewport(vp),
      [],
    ),
  });
  const {
    selectedModuleId,
    selectedEdgeId,
    selectedInterfaceKey,
    activeProcessId,
    setSelectedModuleId,
    setSelectedInterfaceKey,
    setActiveProcessId,
    setBlastRadius,
    currentSelection,
    applySelection,
  } = selection;
  // Hand-placement overrides + undo/redo + persistence — the shared workspace
  // service (PlacementsProvider). The drag mechanics (settle ticker, isDragging,
  // warm-start) live in the layout hook below and are composed into the handlers.
  const {
    placements,
    reset: resetPlacements,
    undo,
    redo,
  } = usePlacementsContext();
  // Board layout — the solver seam, now the shared LayoutProvider service (one
  // solve feeds this chrome's board + the details rail). Owns layoutInputs,
  // globalCtx, the cached `root` build, and all drag/animation machinery. See
  // `useBoardLayout` / `LayoutProvider`.
  const layoutCtl = useLayoutContext();
  const { globalCtx, resetLayout } = layoutCtl;
  // Unified node colouring for the board/minimap/legend. Priority: an ACTIVE
  // VIEW with color styles (S15 — `style { color: <cast> }`, read off the
  // layout's projected plan so the legend can never drift from the board);
  // else aspect "colour by"; else the by-type default. Same NodeColoring the
  // diff board builds from a change set — and the SAME view adapter BoardPane
  // provides to the board/minimap, so every surface agrees.
  const { activeViewName } = useActiveView();
  const { viewPlan, viewNodeIds } = layoutCtl;
  const viewColoring = useMemo<NodeColoring | null>(
    () =>
      viewPlan
        ? buildViewColoring(viewPlan, viewNodeIds, activeViewName ?? undefined)
        : null,
    [viewPlan, viewNodeIds, activeViewName],
  );
  const nodeColoring = useMemo<NodeColoring | null>(
    () =>
      viewColoring ??
      (model
        ? aspectColorKey
          ? buildAspectColoring(model.modules, aspectColorKey)
          : // Default: colour by type — re-applies each node's own (cascaded)
            // colour, so the board is unchanged but gains a by-type legend.
            buildTypeColoring(model.modules)
        : null),
    [model, aspectColorKey, viewColoring],
  );
  // Lifecycle windows, on the same "view plan if there is one, else the model"
  // footing as the colouring above. A bare board has no projection plan but
  // still carries lifecycle, so falling back to the model is what makes the
  // scrubber and the paint appear on Overview and not just inside a view.
  // Scrub-INDEPENDENT by construction: no scrub point is read here, so moving
  // the scrubber never invalidates this memo and never reflows the board.
  const lifecycleWindows = useMemo<ReadonlyMap<string, LifecycleWindow> | undefined>(
    // See BoardPane: the model-derived map is keyed for the BOARD (id /
    // `Space::Path` aliases), which the plan's universe-keyed map is not.
    () => (resolvedModel ? lifecycleWindowsOf(resolvedModel) : undefined),
    [resolvedModel],
  );
  // Global ⌘F search: open flag + hotkey + model index. Owned here (not in
  // the palette) so the shell can add open triggers — the phone header's
  // search button. See `useSearchModal`.
  const searchState = useSearchModal(model);
  // Which list the workspace panel shows. Decoupled from `mode` (the
  // active viewer): clicking a non-board tab swaps only the panel list.
  // Item clicks (process / type) re-couple by flipping mode too.
  const [navTab, setNavTab] = useState<Mode>(mode);
  // Keep the panel list in sync when the viewer changes via item click
  // or URL hash. Tab clicks call setNavTab directly; this effect only
  // catches mode-first mutations.
  useEffect(() => {
    setNavTab(mode);
  }, [mode]);

  // Board double-click → ask the nav to scroll the module's row into view
  // (single board clicks select without scrolling the nav).
  const [navReveal, setNavReveal] = useState<{
    key: string;
    nonce: number;
  } | null>(null);
  const handleBoardItemOpen = useCallback((id: string) => {
    setNavTab("board");
    setNavReveal((p) => ({ key: id, nonce: (p?.nonce ?? 0) + 1 }));
  }, []);
  // Process playback — the workspace-scoped service (scrubber, auto-play,
  // walker state, orchestration + the process actions). Its "locate step"
  // emits `framePair` on the store; the listener below routes that to this
  // board. See `ProcessPlaybackProvider`.
  const processCtl = useProcessPlaybackContext();
  // The `framePair` store event (scrubber "locate step") is now handled by
  // BoardPane directly — it listens and frames its own board. App no longer
  // needs a listener (it would double-frame through the same handle).
  // Dev layout-debug overlay + the global undo/redo + `D` keyboard shortcuts.
  const { debugLayout, setDebugLayout } = useViewerKeyboard({ undo, redo });
  // Right details-panel collapse is remembered PER TAB (each tab keeps its own
  // open/closed choice — e.g. open on the overview board, closed in a file
  // editor). Keyed by tab id in localStorage so it also survives reloads; the
  // controlled pair below mirrors the ACTIVE tab's stored choice and writes
  // back to it. An unseen tab falls back to the boot view-state / width default.
  const activeTabId = useActiveTabId(store);
  // The right details panel is hidden by default (the board carries the model;
  // details are opt-in). A persisted boot choice still wins.
  const detailsCollapsedDefault =
    restoredSidebarCollapsed != null ? restoredSidebarCollapsed : true;
  const [sidebarCollapsed, setSidebarCollapsed] = usePerTabFlag(
    activeTabId,
    detailsCollapsedKey,
    detailsCollapsedDefault,
    store,
  );
  // How an editor surface composes with its companion view ("preview"). Which
  // modes a tab offers, and which it opens at, is declared per tab kind in
  // VIEW_MODE_BY_KIND: code splits beside its board and remembers the last split
  // chosen; a doc opens solo; a process opens full. Each tab then keeps its own
  // override (the singleton doc tab persists across doc switches under id "doc"),
  // and a kind whose default is "sticky" (code) feeds the last choice forward.
  const activeTabKind = useActiveTabKind(store);
  const editorKind: EditorTabKind | null =
    activeTabKind === "file" || activeTabKind === "doc" || activeTabKind === "process"
      ? activeTabKind
      : null;
  const [lastEditorLayout, setLastEditorLayout] = useState<EditorLayout>(() => {
    const raw = hostStorage.getItem(editorLayoutLastKey);
    return isEditorLayout(raw) ? raw : EDITOR_LAYOUT_DEFAULT;
  });
  useEffect(() => {
    hostStorage.setItem(editorLayoutLastKey, lastEditorLayout);
  }, [lastEditorLayout]);
  const kindDefaultLayout = editorKind
    ? defaultLayoutFor(editorKind, lastEditorLayout)
    : lastEditorLayout;
  const [editorLayout, setEditorLayoutPerTab] = usePerTabValue<EditorLayout>(
    activeTabId,
    editorLayoutByTabKey,
    kindDefaultLayout,
    store,
  );
  const editorLayoutOptions = editorKind ? layoutOptionsFor(editorKind) : EDITOR_LAYOUTS;
  const setEditorLayout = useCallback(
    (next: EditorLayout) => {
      setEditorLayoutPerTab(next);
      // A sticky-default kind (code) feeds its last choice forward as the default
      // for future tabs of the kind; doc/process keep their own per-tab choice
      // without moving any shared default (so new processes still open full).
      if (editorKind && VIEW_MODE_BY_KIND[editorKind].stickyDefault) {
        setLastEditorLayout(next);
      }
    },
    [setEditorLayoutPerTab, editorKind],
  );
  // sidebarTab kept only for back-compat with the persisted view state; the
  // right panel auto-picks its content from selection state now (DetailsPane).
  const sidebarTab: SidebarTab = "details";

  // Left nav-panel collapse — owned here (controlled) so the board-chrome
  // toggle and the ResizablePanel wrapper share one state.
  const [navCollapsed, setNavCollapsed] = useState<boolean>(() => {
    return hostStorage.getItem(navCollapsedKey) === "1";
  });
  useEffect(() => {
    hostStorage.setItem(navCollapsedKey, navCollapsed ? "1" : "0");
  }, [navCollapsed]);

  // Focal path + viewport tracking + debounced persistence of
  // viewport/selection/sidebar — the board reports into the store, the
  // focal path is the store's reactive slice. See `useViewPersistence`.
  const { focalPath, handleViewportChange } = useViewPersistence(store, {
    selectedModuleId,
    selectedEdgeId,
    selectedInterfaceKey,
    sidebarCollapsed,
    sidebarTab,
  });

  // Board click: fresh main-view selection. Resets the drill-down stack.
  const handleSelect = useCallback(
    (id: string | null) => {
      applySelection(
        { moduleId: id as StableId | null, edgeId: null },
        "board",
      );
    },
    [applySelection],
  );

  const handleSelectEdge = useCallback(
    (edgeId: string | null) => {
      applySelection({ moduleId: null, edgeId }, "board");
    },
    [applySelection],
  );

  // Sidebar drill-down: select AND fly. Pushes onto the back stack so
  // the user can return to where they came from.
  const handleNavigateModule = useCallback(
    (id: StableId) => {
      applySelection({ moduleId: id, edgeId: null }, "sidebar");
      boardRef.current?.flyTo(id);
    },
    [applySelection],
  );

  // Cross-mode jump from the Types view's Used-by list. We can't call
  // boardRef directly because the board isn't mounted in Types mode —
  // stash the id, switch modes, and an effect below picks it up once
  // the board has mounted.
  const pendingFlyToRef = useRef<StableId | null>(null);
  const handleJumpToInstance = useCallback(
    (id: StableId) => {
      applySelection({ moduleId: id, edgeId: null }, "board");
      pendingFlyToRef.current = id;
      setMode("board");
    },
    [applySelection, setMode],
  );

  useEffect(() => {
    if (mode !== "board") return;
    const id = pendingFlyToRef.current;
    if (!id) return;
    pendingFlyToRef.current = null;
    // The board mounts and runs its own initial-fit ResizeObserver pass
    // before honouring flyTo. Wait one frame for the layout to settle.
    const handle = window.setTimeout(() => {
      boardRef.current?.flyTo(id);
    }, 60);
    return () => window.clearTimeout(handle);
  }, [mode]);

  // Like pendingFlyToRef, but runs an arbitrary navigation closure once the
  // board is mounted. Lets a search pick from another mode (types/process/
  // docs) switch to the board and *then* fly + select.
  const pendingBoardActionRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (mode !== "board") return;
    const action = pendingBoardActionRef.current;
    if (!action) return;
    pendingBoardActionRef.current = null;
    const handle = window.setTimeout(action, 60);
    return () => window.clearTimeout(handle);
  }, [mode]);

  // Run `action` on the board: immediately when it's already showing, else
  // switch to it and defer until it mounts.
  const runOnBoard = useCallback(
    (action: () => void) => {
      if (mode === "board") action();
      else {
        pendingBoardActionRef.current = action;
        setMode("board");
      }
    },
    [mode, setMode],
  );

  const handleNavigateEdge = useCallback(
    (edgeId: string | null) => {
      applySelection({ moduleId: null, edgeId }, "sidebar");
    },
    [applySelection],
  );

  const handleNavigateToModule = useCallback(
    (qualified: string) => {
      const m = findModuleByName(model?.modules ?? [], qualified);
      if (!m?.id) return;
      pendingFlyToRef.current = m.id;
      setMode("board");
    },
    [model, setMode],
  );

  // A native host's nav click reaching the board (`arch:select` / a cold
  // boot's `init.select`): select the entity and put the board on it — the
  // host-side twin of the in-page nav's single click. The ask can arrive
  // BEFORE the workspace has compiled (the host's tree answers from its own
  // server), so an early reveal is held and landed once the model exists.
  const pendingHostRevealRef = useRef<string | null>(null);
  const revealEntity = useCallback(
    (id: string) => {
      if (!model) {
        pendingHostRevealRef.current = id;
        return;
      }
      pendingHostRevealRef.current = null;
      const moduleId = id as StableId;
      runOnBoard(() => {
        applySelection({ moduleId, edgeId: null }, "sidebar");
        // The board may still be running its initial-fit pass (same settle
        // window the cross-mode jump above waits out) — give it a frame.
        window.setTimeout(() => boardRef.current?.flyTo(moduleId), 60);
      });
    },
    [model, runOnBoard, applySelection],
  );
  useEffect(() => {
    if (!model) return;
    const id = pendingHostRevealRef.current;
    if (!id) return;
    revealEntity(id);
  }, [model, revealEntity]);

  const handleBlastRadius = useCallback(() => {
    if (!selectedModuleId) return;
    setBlastRadius(blastRadiusIds(selectedModuleId));
  }, [selectedModuleId, blastRadiusIds]);

  // Drag/place is owned by the board now (BoardPane → the PlacementsProvider
  // service); App no longer wraps place/unpin.

  // "Reset layout": drop the warm-start cache (cold solve) via the layout
  // hook, then clear the saved placements.
  const clearPlacements = useCallback(() => {
    resetLayout();
    resetPlacements();
  }, [resetLayout, resetPlacements]);

  // Structural lookup maps (module/interface/process by id). See
  // `useModelIndexes`.
  const indexes = useModelIndexes(model);

  const selectedModule = selectedModuleId
    ? (indexes?.moduleById.get(selectedModuleId) ?? null)
    : null;

  // A selected aspect-web node (id `aspect:<key>=<json-value>`) isn't a
  // real module — resolve its key/value/colour + members for the inspector.
  const selectedAspect = useMemo(
    () => resolveSelectedAspect(selectedModuleId, model),
    [selectedModuleId, model],
  );

  const selectedInterface = useMemo(
    () => resolveSelectedInterface(selectedInterfaceKey, indexes),
    [selectedInterfaceKey, indexes],
  );

  // Select an interface by its qualified name (`Module.Path.Interface`).
  // Opens the dedicated interface inspector. We also select the owning
  // module so the board keeps context (highlight + back-nav); the
  // inspector prioritises the interface over the module.
  const handleSelectInterface = useCallback(
    (qualified: string) => {
      const owner = indexes?.interfaceById.get(qualified);
      if (owner) {
        applySelection({ moduleId: owner.moduleId, edgeId: null }, "sidebar");
      }
      setSelectedInterfaceKey(qualified);
    },
    [applySelection, indexes],
  );

  // Phone-width layout: panels become bottom tabs (files / viewer / details).
  // Selecting something on the board or in the nav flips to the Details tab —
  // on a phone the details sidebar isn't visible alongside the board, so the
  // selection would otherwise appear to do nothing.
  //
  // The flip is DEBOUNCED past the double-tap window: a double-tap's first
  // tap selects, and flipping immediately would hide the board before the
  // second tap lands (it would hit the details surface instead of diving).
  // An open/dive — or a deselect — cancels the pending flip.
  const {
    isMobile,
    mobileTab,
    setMobileTab,
    scheduleDetailsFlip,
    cancelDetailsFlip,
    showViewer: showMobileViewer,
  } = useMobileLayout(store, { enabled: layout !== "desktop" });
  const selectAndShowDetails = useCallback(
    (id: string | null) => {
      handleSelect(id);
      if (!isMobile) return;
      if (id) scheduleDetailsFlip();
      else cancelDetailsFlip();
    },
    [handleSelect, isMobile, scheduleDetailsFlip, cancelDetailsFlip],
  );
  const selectEdgeAndShowDetails = useCallback(
    (edgeId: string | null) => {
      handleSelectEdge(edgeId);
      if (!isMobile) return;
      if (edgeId) scheduleDetailsFlip();
      else cancelDetailsFlip();
    },
    [handleSelectEdge, isMobile, scheduleDetailsFlip, cancelDetailsFlip],
  );
  const selectInterfaceAndShowDetails = useCallback(
    (qualified: string) => {
      handleSelectInterface(qualified);
      if (isMobile) scheduleDetailsFlip();
    },
    [handleSelectInterface, isMobile, scheduleDetailsFlip],
  );
  // A board open/dive (double-tap) must drop the pending flip its own first
  // tap scheduled — the user asked to go deeper, not for the details panel.
  const openBoardItem = useCallback(
    (id: string) => {
      cancelDetailsFlip();
      handleBoardItemOpen(id);
    },
    [cancelDetailsFlip, handleBoardItemOpen],
  );

  // Image export (PNG/SVG/clipboard) + share-by-link — the store-backed
  // controller (files/selection/viewport from the store; the board ref is
  // the capture handle). See `useExportShareActions`.
  const exportShareCtl = useExportShareActions(store, {
    capture: boardRef,
    label: exportLabel,
    qualifiedNameById: indexes?.moduleQualifiedNameById,
    compare: compareAgainst
      ? {
          vfs: compareAgainst.vfs,
          label: compareAgainst.label,
          swapped: compareSwapped,
        }
      : null,
  });

  // Process activation (history-pushing) lives on the playback service.
  const handleActivateProcess = processCtl.handleActivateProcess;

  // Route a picked search hit to its place, switching modes as needed.
  const handleSearchPick = useCallback(
    (entry: SearchEntry) => {
      const t = entry.target;
      switch (t.kind) {
        case "module":
          runOnBoard(() => {
            applySelection({ moduleId: t.moduleKey, edgeId: null }, "board");
            boardRef.current?.flyTo(t.moduleKey);
          });
          break;
        case "interface":
          runOnBoard(() => {
            boardRef.current?.flyTo(t.moduleKey);
            handleSelectInterface(t.qualifiedName);
          });
          break;
        case "process":
          runOnBoard(() => handleActivateProcess(t.processKey));
          break;
        case "type":
          store.openType(t.name);
          break;
        case "plane":
          runOnBoard(() => plane.select(t.plane));
          break;
      }
    },
    [
      runOnBoard,
      applySelection,
      handleSelectInterface,
      handleActivateProcess,
      store,
      setMode,
      plane,
    ],
  );

  // Subprocess name → rendered-markdown description. ProcessTree's
  // DoBlock reads this to show a short README under an expanded
  // `do X()` invocation. Covers top-level subprocesses plus those
  // nested inside any process body in the workspace.
  // Route a click on an archlang:// link inside a rendered description.
  // The renderer emits five URI forms — `archlang://id/<stableId>`,
  // `archlang://name/<qualifiedName>`, `archlang://doc/<name>#<slug>`, and
  // `archlang://aspect/<key>[:<value>]`. We dispatch to the existing
  // selection handlers (module, process, interface), open the document, or
  // route to the aspect overlay/selection. View targets are currently not
  // selectable from the board, so they're a no-op.
  // The ONE way to open a document: re-point the singleton `doc` tab at its
  // `.md` path (the CM6 DocumentsPane surface — markdown's only surface),
  // optionally scrolling to a section. Shared by the nav's Documents list and
  // `archlang://doc/…` description links, so every entry point behaves alike.
  const openDocument = useCallback(
    (name: string, slug?: string) => {
      const path = documents.find((d) => d.name === name)?.path;
      if (!path) return;
      store.openTab({ kind: "doc", path, slug });
    },
    [store, documents],
  );

  const handleArchlangNav = useCallback(
    (href: string) => {
      const action = resolveArchlangNav(href, model, indexes);
      if (!action) return;
      if (action.kind === "process") handleActivateProcess(action.processId);
      else if (action.kind === "module") handleNavigateModule(action.moduleKey);
      else if (action.kind === "interface")
        handleSelectInterface(action.interfaceId);
      else if (action.kind === "document") {
        openDocument(action.name, action.slug);
      } else if (action.kind === "type") {
        // Same landing a ⌘F / nav-tree pick gives a declared type (r53).
        store.openType(action.name);
      } else if (action.kind === "aspect") {
        // With a value → select the synthetic aspect node (surfaces the
        // aspect inspector via `resolveSelectedAspect`). Without one →
        // colour the board by that aspect key. Both reuse existing state.
        if (action.value !== undefined) {
          applySelection(
            {
              moduleId:
                `aspect:${action.key}=${JSON.stringify(action.value)}` as StableId,
              edgeId: null,
            },
            "sidebar",
          );
        } else if (aspectCtl.aspectKeys.includes(action.key)) {
          aspectCtl.setAspectColorKey(action.key);
        }
      }
    },
    [
      model,
      indexes,
      handleActivateProcess,
      handleNavigateModule,
      handleSelectInterface,
      openDocument,
      store,
      applySelection,
      aspectCtl.aspectKeys,
      aspectCtl.setAspectColorKey,
    ],
  );

  // Per-module / per-interface connection participation, keyed by the board
  // edge/port ids the InfiniteBoard uses for selection. See `buildBoardIndexes`.
  const boardIndexes = useMemo(
    () => buildBoardIndexes(globalCtx, indexes),
    [globalCtx, indexes],
  );

  // Widget-registration warnings (duplicate/conflicting custom-element
  // tags from a bad or duplicated package). The widgetGuard turns what
  // would be a viewer-crashing throw into a recorded warning; surface
  // them alongside model diagnostics so they're visible, not silent.
  // Model diagnostics + widget-registration warnings, for display
  // surfaces (count badge, diagnostics panel). Per-module badges below
  // keep using the model diagnostics only, since widget warnings carry
  // no source span. See `useWidgetDiagnostics`.
  const allDiagnostics = useWidgetDiagnostics(diagnostics);

  // Global edge/port lookup by id, for resolving a selected edge without a
  // solved tree. See `buildEdgeSelectLookup`.
  const edgeSelectLookup = useMemo(
    () => buildEdgeSelectLookup(globalCtx),
    [globalCtx],
  );

  const selectedEdge = useMemo(
    () =>
      selectedEdgeId ? (edgeSelectLookup.get(selectedEdgeId) ?? null) : null,
    [selectedEdgeId, edgeSelectLookup],
  );

  // Wrap the layout recompute actions to also cold-reset the engine so the
  // solver starts from a fully rebuilt model, not just a warm-started layout.
  const recomputeLayout = useCallback(() => {
    void coldRefresh();
    layoutCtl.recomputeLayout();
  }, [coldRefresh, layoutCtl.recomputeLayout]);

  const animateRecompute = useCallback(async () => {
    await coldRefresh();
    return layoutCtl.animateRecompute();
  }, [coldRefresh, layoutCtl.animateRecompute]);

  // Composition root: every hook + derived value above is bundled into a flat
  // bag and handed to the presentational composition. The hook owns state and
  // logic; ViewerComposition owns markup. See `ViewerComposition`.
  return {
    // scalars
    hideWorkspacePanel,
    model,
    plane,
    loading,
    error,
    refresh,
    mode,
    setMode,
    navTab,
    setNavTab,
    boardIndexes,
    allDiagnostics,
    sidebarTab,
    // mobile object
    mobile: {
      isMobile,
      mobileTab,
      setMobileTab,
      showViewer: showMobileViewer,
      showDetailsSoon: scheduleDetailsFlip,
    },
    // selection-derived
    activeProcessId,
    // The raw setters the viewer wrapper feeds to its hash-routing effect.
    setSelectedModuleId,
    setActiveProcessId,
    goBack: selection.goBack,
    canGoBack: selection.canGoBack,
    navHistory: selection.navHistory,
    selectedModule,
    selectedAspect,
    selectedInterface,
    selectedEdge,
    // layout-derived
    root: layoutCtl.root,
    animating: layoutCtl.animating,
    animPaused: layoutCtl.animPaused,
    recomputeLayout,
    animateRecompute,
    toggleAnimPause: layoutCtl.toggleAnimPause,
    animStep: layoutCtl.animStep,
    boardRef,
    clearPlacements,
    placements,
    debugLayout,
    setDebugLayout,
    // aspect-derived
    legendPinnedValue: aspectCtl.legendPinnedValue,
    setLegendHoverValue: aspectCtl.setLegendHoverValue,
    setLegendPinnedValue: aspectCtl.setLegendPinnedValue,
    nodeColoring,
    // lifecycle-derived (§9.1)
    lifecycleWindows,
    timeline: resolvedModel?.timeline,
    // exportShare-derived
    exportingPng: exportShareCtl.exportingPng,
    handleExportPng: exportShareCtl.handleExportPng,
    handleExportSvg: exportShareCtl.handleExportSvg,
    handleCopyImage: exportShareCtl.handleCopyImage,
    handleShareWorkspace: exportShareCtl.handleShareWorkspace,
    handleShareView: exportShareCtl.handleShareView,
    handleShareAt: exportShareCtl.handleShareAt,
    // view-derived
    focalPath,
    handleViewportChange,
    sidebarCollapsed,
    setSidebarCollapsed,
    editorLayout,
    setEditorLayout,
    editorLayoutOptions,
    navCollapsed,
    setNavCollapsed,
    // nav-derived — semantic renames (mobile-aware selection handlers)
    handleSelect: selectAndShowDetails,
    handleSelectEdge: selectEdgeAndShowDetails,
    handleSelectInterface: selectInterfaceAndShowDetails,
    handleNavigateModule,
    handleNavigateEdge,
    handleJumpToInstance,
    handleNavigateToModule,
    revealEntity,
    handleArchlangNav,
    openDocument,
    handleBlastRadius,
    boardHoverStore: hoverStore,
    navReveal,
    handleBoardItemOpen: openBoardItem,
    // search-derived
    searchState,
    searchInputRef,
    onSearchPick: handleSearchPick,
  } as const;
}

/**
 * useViewerController — the VIEWER wrapper around `useWorkbenchController`. Adds
 * the viewer-only concerns the workbench core deliberately omits: source
 * acquisition (share / ?src= / upload / demo via `useWorkspaceSources`), the
 * boot-restored view state, controlled-source mirroring, and `?module=` /
 * `?process=` hash routing. The Studio host skips ALL of this — it mounts
 * `useWorkbenchController` directly over its changeset store.
 */
export function useViewerController(
  props: ViewerAppProps & { readonly boot: ViewerBootSource },
) {
  const {
    mode: surfaceMode = "viewer",
    controlledSources,
    hideWorkspacePanel = false,
    layout,
    boot,
    surfaces = NO_SURFACES,
  } = props;
  const store = useWorkspace();
  const sourceCtl = useWorkspaceSources(store, boot, surfaceMode);
  // A share link's view overrides any persisted state.
  const restoredViewerState = boot.viewerState ?? persistedViewerStateAtBoot();
  // Controlled (host-owned) sources keep the host's label + disable the
  // file-picker/share/persistence chrome; their content lives in the store.
  const sources = useMemo<SourceSlot>(
    () =>
      controlledSources
        ? { vfs: sourceCtl.sources.vfs, label: controlledSources.label }
        : sourceCtl.sources,
    [controlledSources, sourceCtl.sources],
  );
  const canPickSources = controlledSources ? false : sourceCtl.canPickSources;

  const wb = useWorkbenchController({
    hideWorkspacePanel,
    layout,
    exportLabel: sources.label,
    restoredSidebarCollapsed: restoredViewerState?.sidebarCollapsed,
  });

  // Alternate surfaces (the scratch draft surface today) — supplied by the
  // composition root, so app-shell never imports the surface package. See
  // `surface.ts`: `useSurface` is a hook, hence the fixed-length rule.
  const surfaceCtx: SurfaceContext = {
    store,
    enabled: surfaceMode === "viewer" && !controlledSources,
    boot,
    draftPath: DRAFT_PATH,
    loadDraftWorkspace: sourceCtl.loadDraftWorkspace,
    restorePersistedOrDemo: sourceCtl.restorePersistedOrDemo,
  };
  const surfaceCount = useRef(surfaces.length).current;
  if (IS_DEV && surfaceCount !== surfaces.length) {
    throw new Error(
      "`surfaces` must be a module constant: its length changed between renders, " +
        "which breaks hook order (see workbench/surface.ts).",
    );
  }
  const surfaceStates = surfaces.map((s) => s.useSurface(surfaceCtx));

  useControlledSources(store, controlledSources);

  return {
    ...wb,
    // source menu — `sources` mirrors what ViewerShell read (sourceCtl.sources),
    // NOT the controlled-aware `sources` local above.
    sources: sourceCtl.sources,
    setSources: sourceCtl.setSources,
    handleSourcesLoaded: sourceCtl.handleSourcesLoaded,
    handleResetDemo: sourceCtl.handleResetDemo,
    // The raw source-controller `canPickSources` — the main-view SourceDropTarget
    // gates on this (not the controlled-aware prop).
    sourceCtlCanPickSources: sourceCtl.canPickSources,
    canPickSources,
    initialViewport: restoredViewerState?.viewport ?? undefined,
    surfaceStates,
  } as const;
}
